import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToast } from '@heroui/react';
import LauncherButton from './LauncherButton';
import ChatHeader from './ChatHeader';
import TypingIndicator from './TypingIndicator';
import MessageBubbleV2 from './MessageBubbleV2';
import ChatInput from './ChatInput';
import { useAuth } from '../../../../../context/AuthContext';
import { useProwlSession } from '../../../../../lib/prowl/ProwlSessionContext';
import { getProwlPageContext } from '../../../../../lib/prowl/pageContext';
import { executeProwlAction } from '../../../../../lib/prowl/actionAllowlist';
import { resolveProwlRoute } from '../../../../../lib/prowl/routeAllowlist';

const DEFAULT_SUGGESTIONS = [
  'What is HirePanther?',
  'How do I book a service?',
];

/**
 * Prowl v2 (task requirement 1): a single SSE connection to
 * POST /api/v2/prowl/stream, real token deltas (requirement 2), tool
 * activity rendered as visible labelled rows (requirement 3), and
 * generative-UI components rendered via @aplicy-com/prowl-components
 * (requirements 4/6). Mounted behind the prowl.v2.customer flag by
 * ProwlChatWidget.tsx — ProwlChatWidgetLegacy.tsx is the unchanged flag-off
 * path.
 */
const ProwlChatWidgetV2 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { messages, isStreaming, requiresSignIn, send, reset } =
    useProwlSession();

  // NavigateChip (design-spec 1j: the "see all N results" chip appended when
  // the detail-page widget caps ServiceCardList at 3) resolves through a
  // frontend-owned allowlist, same pattern as actionAllowlist.ts — the model
  // names a routeKey, never a URL.
  const handleInteract = (event: string, payload: unknown) => {
    if (event !== 'navigate') return;
    const routeKey = (payload as { routeKey?: string })?.routeKey;
    const path = routeKey ? resolveProwlRoute(routeKey) : undefined;
    if (path) navigate(path);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isStreaming]);

  const handleSend = (text: string) => {
    send(text, getProwlPageContext());
  };

  // Unauthenticated visitors get read tools only (task requirement 7) — the
  // server enforces the tier, this only decides what a *draft* action button
  // does when clicked without a session: prompt sign-in rather than call a
  // write endpoint that would 401 anyway.
  const handleAction = async (actionKey: string, payload: unknown) => {
    if (!isAuthenticated) {
      addToast({
        title: 'Sign in required',
        description: 'Sign in to complete this with Prowl.',
        radius: 'md',
        color: 'warning',
      });
      return;
    }

    const result = await executeProwlAction(actionKey, payload);
    addToast({
      title: result.ok ? 'Done' : 'Something went wrong',
      description: result.ok
        ? 'Your request was submitted.'
        : "That didn't go through — please try again.",
      radius: 'md',
      color: result.ok ? 'success' : 'danger',
    });
  };

  const showSignInPrompt = requiresSignIn && !isAuthenticated;

  return (
    <>
      {!isOpen && <LauncherButton onClick={() => setIsOpen(true)} />}

      {isOpen && (
        <div
          className="fixed right-7 bottom-7 z-50 flex flex-col bg-white rounded-[20px] border border-gray-200 overflow-hidden"
          style={{
            width: '392px',
            maxWidth: 'calc(100vw - 32px)',
            height: '600px',
            maxHeight: 'calc(100vh - 56px)',
            boxShadow: '0 24px 64px rgba(15,23,42,0.24)',
            animation: 'hp-in 0.22s ease',
          }}
        >
          <div
            className="h-1 shrink-0"
            style={{
              background:
                'linear-gradient(90deg, var(--chat-accent, #317690), var(--brand-navy, #163544))',
            }}
          />

          <ChatHeader onReset={reset} onClose={() => setIsOpen(false)} />

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-[18px] flex flex-col gap-3 bg-gray-50"
          >
            {messages.map((msg) => (
              <MessageBubbleV2
                key={msg.id}
                message={msg}
                onAction={handleAction}
                onInteract={handleInteract}
              />
            ))}

            {isStreaming && messages[messages.length - 1]?.text === '' && (
              <TypingIndicator />
            )}

            {showSignInPrompt && (
              <div className="max-w-[84%] self-start text-[12.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Sign in to book, request a quote, or pick up where you left off
                next time.
              </div>
            )}

            {!isStreaming && messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 mt-0.5">
                {DEFAULT_SUGGESTIONS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => handleSend(label)}
                    className="px-3.5 py-2 border border-gray-200 rounded-full text-[13px] text-gray-700 bg-white hover:border-gray-300 hover:text-gray-900 transition-colors cursor-pointer"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <ChatInput onSend={handleSend} disabled={isStreaming} />
        </div>
      )}
    </>
  );
};

export default ProwlChatWidgetV2;
